import { type ElementType, forwardRef, type HTMLAttributes } from 'react';

import { cva, type VariantProps } from 'cva';

import styles from './stack.module.css';

export const stackVariants = cva({
  base: styles.stack,
  variants: {
    direction: {
      row: styles.row,
      column: styles.column,
    },
    gap: {
      none: undefined,
      s: styles.gap_s,
      m: styles.gap_m,
      l: styles.gap_l,
      xl: styles.gap_xl,
      '2xl': styles.gap_2xl,
      '3xl': styles.gap_3xl,
      '4xl': styles.gap_4xl,
    },
    alignItems: {
      stretch: styles.align_items_stretch,
      start: styles.align_items_start,
      center: styles.align_items_center,
      end: styles.align_items_end,
    },
    justifyContent: {
      start: styles.justify_content_start,
      center: styles.justify_content_center,
      end: styles.justify_content_end,
      'space-between': styles.justify_content_space_between,
      'space-around': styles.justify_content_space_around,
      'space-evenly': styles.justify_content_space_evenly,
    },
    wrap: {
      wrap: styles.wrap_wrap,
      nowrap: styles.wrap_nowrap,
    },
    fullHeight: { true: styles.full_height },
    fullWidth: { true: styles.full_width },
  },
  defaultVariants: {
    direction: 'column',
    gap: 'm',
    alignItems: 'stretch',
    justifyContent: 'start',
    wrap: 'nowrap',
  },
});

export type StackProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof stackVariants> & {
    /** The HTML element to render as the container. Defaults to `div`. */
    elementType?: ElementType;
  };

/**
 * Stack is a simple layout component that arranges its children in a vertical
 * or horizontal stack, using flexbox.
 *
 * It allows you to control the direction, gap between children, alignment and
 * justification of children.
 *
 * You can use the `stackVariants` helper to create a custom component with the
 * props and styling from Stack.
 *
 * For 2D layouts, use the Grid component.
 *
 * ```jsx
 * <Stack direction="row" gap="m" alignItems="center" justifyContent="end">
 *  <Button>Cancel</Button>
 *  <Button>Save</Button>
 * </Stack>
 * ```
 */
export const Stack = forwardRef<HTMLDivElement, StackProps>(
  (
    {
      elementType: Element = 'div',
      direction,
      gap,
      alignItems,
      justifyContent,
      wrap,
      className,
      fullHeight,
      fullWidth,
      ...props
    },
    ref,
  ) => {
    const classes = stackVariants({
      direction,
      gap,
      alignItems,
      justifyContent,
      wrap,
      className,
      fullHeight,
      fullWidth,
    });

    return <Element ref={ref} {...props} className={classes} />;
  },
);
